import { useCallback, useEffect } from "react"
import { FormattedMessage, useIntl } from "react-intl"
import { useDispatch, useSelector } from "react-redux"
import { Action, Dispatch } from "redux"
import Grid from "@mui/material/Grid"
import { Box, Fade, styled, Typography } from "@mui/material"
import { get } from "lodash"
import { lightblue, white } from "~/constants/colors"
import { API_SERVER_USER, IS_TEC_DEMO_SYSTEM } from "~/constants/api"
import Spinner from "~/components/Spinner/Spinner"
import useDocumentTitle from "~/hooks/useDocumentTitle"
import { PlaneusLogo } from "~/theme/Planeus"
import { get as getHttp } from "~/utils/http"
import { appInit } from "../../actions/app"
import InfoLabel from "~/components/InfoLabel/InfoLabel"
import { UserState } from "../../types/ReduxStateExtensions"

const Root = styled(Box)({
  display: "flex",
  width: "100vw",
  height: "100vh",
  flexGrow: 1,
  backgroundColor: lightblue,
  background: "linear-gradient(150deg, #00a5e5 -10%, #00346e 100%)",
})

const CenteredSpinnerContainer = styled(Box)({
  position: "relative",
  display: "inline-block",
})

const BackendStarting = () => {
  const salesRecover = useSelector<UserState, boolean>(state => get(state, "user.authentication.salesRecover", false))
  const intl = useIntl()
  const dispatch: Dispatch<Action> = useDispatch()
  const fetchInterval = salesRecover ? 1000 * 10 : 1000 * 30
  const messageId = salesRecover ? "salesRecover.spinnerText" : "pages.backendStarting.pleaseWait"

  const checkIsBackendOnline = useCallback(() => {
    getHttp({
      url: `${API_SERVER_USER}/users/check`,
      token: null,
    }).catch(() => {
      dispatch(appInit())
    })
  }, [dispatch])

  useEffect(() => {
    const interval = setInterval(() => checkIsBackendOnline(), fetchInterval)
    return () => clearInterval(interval)
  }, [checkIsBackendOnline, fetchInterval])

  useDocumentTitle(intl.formatMessage({ id: "modules.login" }))
  return (
    <Root>
      <InfoLabel isAppBar={false} />
      <Fade timeout={800} in>
        <Grid container direction="column" justifyContent="center" alignItems="center" flexGrow={1}>
          <Grid item>
            <CenteredSpinnerContainer>
              <Spinner thickness={4} disableShrink={false} size={140} />
              <Box
                sx={{
                  position: "absolute",
                  top: "50%",
                  left: "50%",
                  transform: "translate(-50%, -50%)",
                }}
              >
                <PlaneusLogo height={70} />
              </Box>
            </CenteredSpinnerContainer>
          </Grid>
          <Grid item mt={15}>
            <Typography variant="h6" color={white} textAlign="center">
              <FormattedMessage
                id={messageId}
                values={
                  salesRecover
                    ? {
                        lineBreak: <br />,
                        isTecDemo: IS_TEC_DEMO_SYSTEM,
                      }
                    : null
                }
              />
            </Typography>
          </Grid>
        </Grid>
      </Fade>
    </Root>
  )
}

export default BackendStarting
